/* eslint-disable @typescript-eslint/naming-convention */
import { storyblokEditable } from '@storyblok/react';
import { motion } from 'framer-motion';
import { FC } from 'react';

import Button from '../Button';
import ButtonArrow from '../ButtonArrow';
import Heading from '../Heading';
import PreHeading from '../PreHeading';
import Prose from '../Prose';

export interface StandardTextProps {
  blok: any;
  size?: StandardTextSize;
  pre_headline?: any;
  headline?: any;
  content?: any;
  actions?: any;
}

export enum StandardTextSize {
  XL,
  LG,
  ML,
  MD,
  SM,
  XS,
}

const SIZE = {
  [StandardTextSize.XL]: {
    preheadingSize: 'lg',
    headingSize: 'xl',
    proseSize: 'xl',
  },
  [StandardTextSize.LG]: {
    preheadingSize: 'lg',
    headingSize: 'xl',
    proseSize: 'xl',
  },
  [StandardTextSize.ML]: {
    preheadingSize: 'md',
    headingSize: 'lg',
    proseSize: 'base',
  },
  [StandardTextSize.MD]: {
    preheadingSize: 'md',
    headingSize: 'md',
    proseSize: 'base',
  },
  [StandardTextSize.SM]: {
    preheadingSize: 'sm',
    headingSize: 'sm',
    proseSize: 'md',
  },
  [StandardTextSize.XS]: {
    preheadingSize: 'sm',
    headingSize: 'xs',
    proseSize: 'md',
  },
};

const StandardText: FC<StandardTextProps> = ({
  blok,
  pre_headline = blok?.pre_headline,
  headline = blok?.headline,
  content = blok?.content,
  actions = blok?.actions,
  size = StandardTextSize.SM,
}) => {
  if (!blok) return null;

  const { preheadingSize, headingSize, proseSize } = SIZE[size];

  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.25,
        delayChildren: 0.35,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 25 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 75,
        mass: 0.75,
        duration: 0.35,
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      variants={container}
      className="space-y-8"
    >
      <div {...storyblokEditable(blok)} className="space-y-4">
        <motion.div variants={item} className="space-y-2">
          {pre_headline && (
            <PreHeading
              dot={true}
              size={preheadingSize}
              element="h2"
              color="primary"
              className="font-extralight"
            >
              {pre_headline}
            </PreHeading>
          )}
          {headline && (
            <Heading
              size={headingSize}
              weight="semi"
              element={pre_headline ? 'p' : 'h2'}
            >
              {headline}
            </Heading>
          )}
        </motion.div>
        {content && (
          <motion.div variants={item}>
            <Prose size={proseSize} content={content}></Prose>
          </motion.div>
        )}
      </div>
      {actions?.length ? (
        <motion.div variants={item} className="flex flex-wrap gap-x-3 gap-y-2">
          {actions.map((action) => (
            <Button key={action._uid} link={action.link} style={action.style}>
              <ButtonArrow className={`${action.style} -ml-1 mr-1 h-5 w-6`} />
              <span className="-mb-0.5">{action.label}</span>
            </Button>
          ))}
        </motion.div>
      ) : null}
    </motion.div>
  );
};

export default StandardText;
